import {isLLC} from "./urls";
import {WEBAUTHN_UPDATED_RPID_LS_KEY, WEBAUTHN_UPDATED_RPID_LS_VALUE} from "../constants";
import {postDataToEventBus} from "./index";

export const isDeviceWebAuthnCapable = () => {
  if (window.PublicKeyCredential) {
    return window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
      .then((available) => {
        if (available) {
          return true
        } else {
        // WebAuthn Supported. Platform authenticator does not support.
          return false
        }
      })
      .catch(() => false)
  } else {
    return false
  }
}

export const WebauthnException = (message, metadata) => {
  const error = new Error(message);
  error.metadata = metadata;
  return error;
}

export const showWebauthnUpgrade = (email) => {
  try {
    // checks LS to see if sleeping timer has expired before attempting to re-prompt webauthn upgrade. returns boolean.
    const sanitizedEmail = email.trim().toLowerCase();
    const currentUnixDate = new Date();
    const currentUnixTime = currentUnixDate.getTime();
    const persistedObj = JSON.parse(localStorage.getItem("FPSI.webauthn")) || {};
    const persistedUuid = persistedObj[sanitizedEmail]?.frUuid;
    // Don't show WebAuthn upgrade prompt if user is already enrolled on current device
    if (persistedUuid) {
      return false;
    }
    const persistedTimer = persistedObj[sanitizedEmail]?.noThanks || 0;
    if (typeof persistedTimer === "number") {
      return ((persistedTimer - currentUnixTime) < 0 )
    }
    return false;
  } catch(e) {
    throw new WebauthnException(e, {email,"misc":"showWebauthnUpgrade"});
  }
}

export const performCleansingOfWebAuthnLocalStorage = () => {
  const lowerCasedCredentials = {}
  try {
    const webauthnInfo = JSON.parse(localStorage.getItem("FPSI.webauthn"));
    if(webauthnInfo!==null) {
      const emails = Object.keys(webauthnInfo);
      emails.forEach((email)=>{
        lowerCasedCredentials[email.toLowerCase()] = webauthnInfo[email];
      })
      localStorage.setItem("FPSI.webauthn", JSON.stringify(lowerCasedCredentials));
    }
  } catch(e) {
    throw new WebauthnException(e, { "misc": "Unable to perform the cleansing"});
  }
}

const getUpdatedWebAuthnDataAfterNoThanks = (email, oldWebAuthnData) => {
  const WEBAUTHN_UPGRADE_SLEEP_TIMER = 90;
  const currentDate = new Date();
  const doNotDisturbUntil = currentDate.setDate(currentDate.getDate() + WEBAUTHN_UPGRADE_SLEEP_TIMER);
  const sanitizedEmail = email.trim().toLowerCase();
  const noThanksPayload = {
    "frUuid": null,
    "noThanks": doNotDisturbUntil
  };
  return {
    ...oldWebAuthnData,
    [sanitizedEmail]: noThanksPayload
  }
}

export const eraseAndUpdateNativeWebAuthnData = (email, oldWebAuthnData) => {
  try {
    delete oldWebAuthnData[email];
    return {
      ...oldWebAuthnData,     
    }
  } catch(e) {
    throw new WebauthnException(e, { email, "misc":"clearEmailWebauthnInfoNative"});
  }
}

export const setWebauthnNoThanksWeb = (email) => {
  try {
    const persistedObj = JSON.parse(localStorage.getItem("FPSI.webauthn")) || {};
    const updatedObj = getUpdatedWebAuthnDataAfterNoThanks(email, persistedObj);
    localStorage.setItem("FPSI.webauthn", JSON.stringify(updatedObj));
  } catch(e) {
    throw new WebauthnException(e, { email, "misc":"setWebauthnNoThanksWeb"});
  }
};

export const setWebauthnNoThanksNative = (email, nativeWebAuthnData, setNativeWebAuthnData) => {
  const newNativeWebAuthnData = getUpdatedWebAuthnDataAfterNoThanks(email, nativeWebAuthnData);
  setNativeWebAuthnData(newNativeWebAuthnData);
  postDataToEventBus("SET_WEBAUTHN_PREF", {"localStorageObject": newNativeWebAuthnData});
};

export const clearWebAuthnInfoNative = (email, nativeWebAuthnData, setNativeWebAuthnData) => {
  const sanitizedEmail = email.trim().toLowerCase();
  const newNativeWebAuthnData  = eraseAndUpdateNativeWebAuthnData(sanitizedEmail, nativeWebAuthnData);
  setNativeWebAuthnData(newNativeWebAuthnData);
  postDataToEventBus("SET_WEBAUTHN_PREF", {"localStorageObject": newNativeWebAuthnData});
}

export const updateEmailForWebAuthnCredentials = (email, previousEmail) => {
  if(email && previousEmail && localStorage.getItem("FPSI.webauthn") && isEmailEnrolledInWebauthn(previousEmail)) {
    try {
      // Get the existing WebAuthn enrollment payload from LS
      const persistedObj = JSON.parse(localStorage.getItem("FPSI.webauthn")) || {};
      const lowerCasedEmailPreviousEmail = previousEmail.toLowerCase();
      const enrollmentPayload = persistedObj[lowerCasedEmailPreviousEmail];
      // Sanitized the new email
      const sanitizedEmail = email.trim().toLowerCase();
      // Link the new email with the existing creadential.
      persistedObj[sanitizedEmail] = {...enrollmentPayload};
      // delete the previous credential.
      delete persistedObj[lowerCasedEmailPreviousEmail];
      localStorage.setItem("FPSI.webauthn", JSON.stringify(persistedObj));
    } catch(e) {
      throw new WebauthnException(e, { email, "misc": previousEmail});
    }
  }
}

export const setWebauthnEnrollmentSuccess = (email, frUuid, frState) => {
  try {
    const sanitizedEmail = email.trim().toLowerCase();
    const frScript = getScriptFromFrState(frState);
    // Set flag if the WebAuthn credential was created with an updated Relying Party ID (in the ForgeRock script)
    const credentialCreatedWithUpdatedRelyingPartyID = containsUpdatedRelyingPartyID(frScript);
    const enrollmentPayload = {
      frUuid,
      "noThanks": 0,
      ...(credentialCreatedWithUpdatedRelyingPartyID && {[WEBAUTHN_UPDATED_RPID_LS_KEY]: WEBAUTHN_UPDATED_RPID_LS_VALUE})
    };

    const persistedObj = JSON.parse(localStorage.getItem("FPSI.webauthn")) || {};
    persistedObj[sanitizedEmail] = enrollmentPayload;
    localStorage.setItem("FPSI.webauthn", JSON.stringify(persistedObj));
  } catch(e) {
    throw new WebauthnException(e, { email, "misc":frUuid});
  }
}

export const setNativeWebAuthnEnrollmentSuccess = (nativeWebAuthnData, email, frUuid) => {
  try {
    const sanitizedEmail = email.trim().toLowerCase();
    const enrollmentPayload = {
      frUuid,
      "noThanks": 0,
    };
    const newNativeWebAuthnData = {
      ...nativeWebAuthnData,
      [sanitizedEmail]: enrollmentPayload,
    };
    postDataToEventBus("SET_WEBAUTHN_PREF", {"localStorageObject": newNativeWebAuthnData});
    return newNativeWebAuthnData;
  } catch(e) {
    throw new WebauthnException(e, { email, "misc":frUuid});
  }
}

export const isEmailEnrolledInWebauthn = (email, nativeWebAuthnData) => {
  try {
    const webauthnInfo = nativeWebAuthnData || JSON.parse(localStorage.getItem("FPSI.webauthn")) || {};
    const lowerCasedEmail = email.toLowerCase();
    return !!(webauthnInfo[lowerCasedEmail]?.frUuid);
  } catch(e) {
    throw new WebauthnException(e, { email, "misc":"isEmailEnrolledInWebauthn"});
  }
}

export const isAnyEmailEnrolledInWebauthn = () => {
  try {
    const webauthnInfo = JSON.parse(localStorage.getItem("FPSI.webauthn")) || {};
    let isAnyEmailEnrolled = false;
    Object.values(webauthnInfo).forEach((infoForEmail) => {
      if (infoForEmail.frUuid) {
        isAnyEmailEnrolled = true;
      }
    });
    return isAnyEmailEnrolled;
  } catch(e) {
    throw new WebauthnException(e, { "misc":"isAnyEmailEnrolledInWebauthn"});
  }
}

export const getWebauthnId = (email) => {
  try {
    const webauthnInfo = JSON.parse(localStorage.getItem("FPSI.webauthn")) || {};
    const lowerCasedEmail = email.toLowerCase();
    return webauthnInfo[lowerCasedEmail]?.frUuid;
  } catch(e) {
    throw new WebauthnException(e, { email, "misc":"getWebauthnID"});
  }
}

export const clearEmailWebauthnInfo = (email) => {
  try {
    const sanitizedEmail = email.trim().toLowerCase();
    const persistedObj = JSON.parse(localStorage.getItem("FPSI.webauthn")) || {};
    delete persistedObj[sanitizedEmail];
    localStorage.setItem("FPSI.webauthn", JSON.stringify(persistedObj));
  } catch(e) {
    throw new WebauthnException(e, { email, "misc":"clearEmailWebauthnInfo"});
  }
}

export const getTPMsupport = () => window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable().then((e) => e);

const isConditionalMediationAvailable = () => {
  if(typeof window.PublicKeyCredential.isConditionalMediationAvailable === "function") {
    return window.PublicKeyCredential?.isConditionalMediationAvailable().then( (e) => e )
  }
  return false;
}

export const getWebauthnDeviceProfile =  async (email) => {
  if(typeof (window.PublicKeyCredential) !== "undefined") {
    const tpmSupport = await getTPMsupport();
    const passkeySupport = await isConditionalMediationAvailable();
    return {
      email,
      tpmSupport,
      passkeySupport
    }
  }
  return {
    email,
    "tpmSupport": false,
    "passkeySupport": false
  }
}

export const extractCredentialIdFromAttestationResult = (attestationResult) => {
  let credentialId = "";
  const splitResult = attestationResult.split("::");
  // For attestation results, the credential ID should be the final value after splitting on the '::' separator
  if (splitResult.length > 1) {
    credentialId = splitResult[splitResult.length-1];
  }
  return credentialId;
}

export const extractCredentialIdFromAssertionResult = (assertionResult) => {
  let credentialId = "";
  const splitResult = assertionResult.split("::");
  // For assertion results, the credential ID should be the penultimate value after splitting on the '::' separator
  if (splitResult.length > 2) {
    credentialId = splitResult[splitResult.length-2];
  }
  return credentialId;
}

/**
   * Determine if webauthn flow should be shown to user depending if
   *  user is in checkout signin 
   *  webauthn is enabled in the backend
   *  webauthn is enabled for checkout signin
   * @param Boolean isCheckoutPage 
   * @param Boolean isWebAuthnFSEnabled 
   * @param Boolean isWebAuthnCheckoutDisabled 
   * @returns Boolean to enable webauthn in FE
   */
export const isWebAuthnFlowEnabled = (isCheckoutPage, isWebAuthnFSEnabled, isWebAuthnCheckoutDisabled) => {
  const isCheckoutWebAuthnDisabled = isCheckoutPage && isWebAuthnCheckoutDisabled;
  return isWebAuthnFSEnabled && !isCheckoutWebAuthnDisabled;
};


export const getScriptFromFrState = (frState) => {
  return frState?.callbacks[0]?.output[0]?.value || "";
}


export const containsUpdatedRelyingPartyID = (script) => {
  const updatedRelyingPartyId = isLLC() ? "homedepotdev.com" : "www.homedepot.com";
  return script?.includes(`d: "${updatedRelyingPartyId}"`);
};
